<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
// NOTICE: KAPTION-INTEGRATION - Favorites
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  // NOTICE: KAPTION-INTEGRATION - Optional star props for sidebar starring
  starType: { type: String, default: null },
  starId: { type: [Number, String], default: null },
  // NOTICE: KAPTION-INTEGRATION - Optional explanatory tooltip (filter definitions)
  tooltip: { type: String, default: null },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();
// NOTICE: KAPTION-INTEGRATION - Favorites
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();
const {
  isFavorite,
  isAccountFavorite,
  isPersonalFavorite,
  togglePersonalFavorite,
  toggleAccountFavorite,
} = useKaptionFavorites();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

// NOTICE: KAPTION-INTEGRATION - Check if this item can be starred
// feature-permissions.NAVIGATION.3
const canStar = computed(() => {
  return (
    isKaptionFlagEnabled('KAPTION_SIDEBAR_STARRING') &&
    canUseFeature('sidebar_starring') &&
    props.starType &&
    props.starId != null
  );
});

// NOTICE: KAPTION-INTEGRATION - Check if this item is starred
const itemIsFavorite = computed(() => {
  if (!canStar.value) return false;
  return isFavorite(props.starType, props.starId);
});

const itemIsAccountFavorite = computed(() => {
  if (!canStar.value) return false;
  return isAccountFavorite(props.starType, props.starId);
});

const itemIsPersonalFavorite = computed(() => {
  if (!canStar.value) return false;
  return isPersonalFavorite(props.starType, props.starId);
});

// NOTICE: KAPTION-INTEGRATION - Toggle favorite status
const handleStarClick = async event => {
  event.preventDefault();
  event.stopPropagation();
  if (!canStar.value) return;

  // If this is an account favorite, non-admins can't change it.
  if (itemIsAccountFavorite.value && !isAdmin.value) return;

  // Admins can toggle account favorites with Shift-click.
  if (isAdmin.value && event.shiftKey) {
    await toggleAccountFavorite(props.starType, props.starId);
    return;
  }

  togglePersonalFavorite(props.starType, props.starId);
};

// NOTICE: KAPTION-INTEGRATION - Tooltip text
const starTooltip = computed(() => {
  if (itemIsAccountFavorite.value && !itemIsPersonalFavorite.value) {
    return isAdmin.value
      ? t('SIDEBAR.ACCOUNT_FAVORITE_ADMIN_HINT')
      : t('SIDEBAR.ACCOUNT_FAVORITE');
  }
  return itemIsFavorite.value
    ? t('SIDEBAR.UNSTAR_ITEM')
    : t('SIDEBAR.STAR_ITEM');
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-3 rtl:pr-3 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - explanatory tooltip wins over the title attr -->
    <component
      :is="to ? 'router-link' : 'div'"
      v-tooltip.right="tooltip || undefined"
      :to="to"
      :title="tooltip ? undefined : label"
      class="flex h-8 items-center gap-2 px-2 py-1 rounded-2xl max-w-[9.438rem] hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group"
      :class="{
        'text-n-blue-text bg-n-alpha-2 active': active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0">{{ label }}</div>
      </template>
      <!-- NOTICE: KAPTION-INTEGRATION - Star toggle button -->
      <button
        v-if="canStar"
        v-tooltip.top="starTooltip"
        type="button"
        class="flex-shrink-0 size-4 p-0 flex items-center justify-center rounded transition-opacity"
        :class="{
          'opacity-100 text-yellow-500': itemIsPersonalFavorite,
          'opacity-100 text-n-blue-text':
            itemIsAccountFavorite && !itemIsPersonalFavorite,
          'opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-yellow-500':
            !itemIsFavorite,
        }"
        @click="handleStarClick"
      >
        <span
          :class="itemIsFavorite ? 'i-lucide-star' : 'i-lucide-star'"
          class="size-3.5"
          :style="itemIsFavorite ? 'fill: currentColor' : ''"
        />
      </button>
    </component>
  </Policy>
</template>
